import moment from "moment-timezone";
import { NavigateFunction } from "react-router-dom";
import { CONSTANTS } from "src/constants";
import { COMMON_CONSTANTS } from "src/constants/commonConstants";
import { SIDEBAR_CONSTANTS } from "src/constants/sidebarConstants";
import { URL_CONSTANTS } from "src/constants/url.constants";
import { commonLocales } from "src/locales/commonLocales";
import { NotificationProps, ObjectProps } from "src/models/common.model";
import API_SERVICES from "src/services";

export function formatTimestamp(timestamp: string, skipTz?: boolean): string {
  // parseZone keeps the offset already on the backend string.
  const targetDate = skipTz
    ? moment.parseZone(timestamp)
    : moment(timestamp).tz(
        process.env.REACT_APP_ADMIN_TIMEZONE ?? CONSTANTS.DEFAULT_TIMEZONE
      );

  const currentDate = skipTz
    ? moment().utcOffset(targetDate.utcOffset())
    : moment().tz(
        process.env.REACT_APP_ADMIN_TIMEZONE ?? CONSTANTS.DEFAULT_TIMEZONE
      );

  const isToday =
    targetDate.format("YYYY-MM-DD") === currentDate.format("YYYY-MM-DD");

  if (isToday) {
    const formatTime = targetDate.format("hh:mm A");
    return `${commonLocales.today} - ${formatTime}`;
  } else {
    return targetDate.format("MM/DD/YYYY");
  }
}

export const getData = (notification: NotificationProps, key: string) => {
  const message = (notification?.notification as ObjectProps)?.message;
  if (key === COMMON_CONSTANTS.TITLE) {
    return typeof message === COMMON_CONSTANTS.DATA_TYPE.OBJECT
      ? ((message as ObjectProps).title as string)
      : message;
  } else if (key === COMMON_CONSTANTS.CONTENT) {
    return typeof message === COMMON_CONSTANTS.DATA_TYPE.OBJECT
      ? ((message as ObjectProps).content as string)
      : CONSTANTS.COMMON_CONSTANTS.EMPTY_VALUE;
  } else if (key === COMMON_CONSTANTS.DATE) {
    return typeof (notification?.created_at as string) ===
      COMMON_CONSTANTS.DATA_TYPE.STRING
      ? formatTimestamp(notification?.created_at as string, true)
      : null;
  }
};

export const goPage = async (
  type: number | string,
  id: number | string,
  navigate: NavigateFunction,
  messageStatus: {
    is_read: boolean;
    uuid: string;
  },
  allowedUrls?: (string | undefined)[],
  role?: number
) => {
  !messageStatus?.is_read &&
    (await API_SERVICES.COMMON.UPDATE_NOTIFICATION(messageStatus?.uuid));
  if (
    CONSTANTS.NOTIFICATION.TYPE.NOTIFICATION_OFFICE_PROFILE ===
      (type as number) &&
    allowedUrls?.includes(SIDEBAR_CONSTANTS.ROUTE_OFFICE_USERS)
  ) {
    navigate(`${URL_CONSTANTS.OFFICE_PROFILE}/${id}`);
  } else if (
    CONSTANTS.NOTIFICATION.TYPE.NOTIFICATION_PROFESSIONAL_PROFILE ===
      (type as number) &&
    allowedUrls?.includes(SIDEBAR_CONSTANTS.ROUTE_PROFESSIONAL_USERS)
  ) {
    navigate(`${URL_CONSTANTS.PROFESSIONAL_PROFILE}/${id}`);
  } else if (
    CONSTANTS.NOTIFICATION.TYPE.NOTIFICATION_JOB === (type as number) &&
    allowedUrls?.includes(SIDEBAR_CONSTANTS.NOTIFICATION_URL)
  ) {
    navigate(`${URL_CONSTANTS.JOB_VIEW}/${id}`);
  } else if (
    CONSTANTS.NOTIFICATION.TYPE.TIMESHEET_DISPUTE === (type as number) &&
    allowedUrls?.includes(SIDEBAR_CONSTANTS.TIMESHEET_ROUTE)
  ) {
    navigate(`${URL_CONSTANTS.PROFESSIONAL_TIMESHEET_VIEW}/${id}`);
  } else if (
    CONSTANTS.NOTIFICATION.TYPE.NOTIFICATION_NOTES === (type as number) ||
    CONSTANTS.NOTIFICATION.TYPE.TIMESHEET_NOTES_PROFESSIONAL ===
      (type as number) ||
    CONSTANTS.NOTIFICATION.TYPE.NOTES_COMPLETE === (type as number) ||
    CONSTANTS.NOTIFICATION.TYPE.NOTES_POSTPONE === (type as number)
  ) {
    navigate(URL_CONSTANTS.NOTES);
  } else if (
    CONSTANTS.NOTIFICATION.TYPE.INVOICE_PAYMENT === (type as number) &&
    allowedUrls?.includes(SIDEBAR_CONSTANTS.ROUTE_INVOICE)
  ) {
    navigate(`${URL_CONSTANTS.INVOICE}/${id}`);
  } else if (
    CONSTANTS.NOTIFICATION.TYPE.PROSPECT === (type as number) &&
    allowedUrls?.includes(SIDEBAR_CONSTANTS.ROUTE_PROSPECTS)
  ) {
    navigate(`${URL_CONSTANTS.PROSPECT_VIEW}/${id}`, {
      state: { from: location.pathname + location.search },
    });
  } else if (
    CONSTANTS.NOTIFICATION.TYPE.RECRUITER_ASSIGNMENT === (type as number) &&
    allowedUrls?.includes(SIDEBAR_CONSTANTS.NOTIFICATION_URL)
  ) {
    role === CONSTANTS.SUPER_ADMIN || role === CONSTANTS.ADMIN_USER
      ? navigate(`${URL_CONSTANTS.JOB_ASSIGNMENTS}?tabIndex=1`)
      : navigate(`${URL_CONSTANTS.JOB_ASSIGNMENTS}`);
  }
};

export const readNotifications = async (
  notifications: NotificationProps[],
  uuid: string,
  setNotifications: (data: NotificationProps[]) => void,
  navigate: NavigateFunction,
  allowedUrls?: (string | undefined)[],
  role?: number
) => {
  const notificationIndex = notifications.findIndex(
    (item: ObjectProps) => item.uuid === uuid
  );

  if (notificationIndex !== CONSTANTS.NOT_FOUND_VALUE) {
    const notification = notifications[notificationIndex];
    const updatedNotifications = [...notifications];
    updatedNotifications[notificationIndex] = {
      ...updatedNotifications[notificationIndex],
      is_read: CONSTANTS.COMMON_CONSTANTS.YES,
    };
    setNotifications(updatedNotifications);
    goPage(
      ((notification?.notification as ObjectProps)?.meta_data as ObjectProps)
        ?.type as string,
      ((notification?.notification as ObjectProps)?.meta_data as ObjectProps)
        ?.uuid as string,
      navigate,
      {
        is_read: (notification as ObjectProps)?.is_read as boolean,
        uuid: (notification as ObjectProps)?.uuid as string,
      },
      allowedUrls,
      role ?? 0
    );
  }
};
